import { format, isAfter, parse, sub } from "date-fns";

/**
 * Formats a Date object into a "YYYY-MM-DD" string.
*/
export const dobToString = (dob: Date | null): string => {
    if (!dob) {
        return "";
    }
    return format(dob, "yyyy-MM-dd");
};

/**
 * Checks if a given month and year is less than 12 months from the current date.
 * @param monthStr The full name of the month (e.g., "September").
 * @param yearStr The full year as a string (e.g., "2024").
 * @returns True if the date is within the last 12 months, otherwise false.
*/
export const isLessThan12Months = (monthStr: string, yearStr: string): boolean => {
    const moveInDate = parse(`${monthStr} ${yearStr}`, "MMMM yyyy", new Date());
    const twelveMonthsAgo = sub(new Date(), { months: 12 });
    return isAfter(moveInDate, twelveMonthsAgo);
};

/**
 * Returns the current date in "YYYY-MM" format.
*/
export const getCurrentYearMonth = (): string => {
    return format(new Date(), "yyyy-MM");
};

/**
 * Formats day, month, and year strings into a "YYYY-MM-DD" string.
*/
export const formatDOB = (day: string, month: string, year: string): string => {
    const paddedDay = day?.trim().padStart(2, "0");
    const paddedMonth = month?.trim().padStart(2, "0");
    return `${year?.trim()}-${paddedMonth}-${paddedDay}`;
};

/**
 * Converts a month name to its numeric representation (01-12).
 * @param monthName The full name of the month (e.g., "January").
 * @returns The zero-padded month number (e.g., "01" for January).
*/
export const monthNameToNumber = (monthName: string): string => {
    if (!monthName) return "";
    const trimmedMonth = monthName.trim();
    const date = parse(trimmedMonth, "MMMM", new Date());
    return String(date.getMonth() + 1).padStart(2, "0");
};
